import { ChangeDetectionStrategy, Component, OnInit, Signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { WebsiteConfigFormExpansionPanelComponent } from '../website-config-form-expansion-panel/website-config-form-expansion-panel.component';
import { WebsiteStateService } from '../../../../../state-management/services/website-state.service';
import { BusinessConfiguration, NavbarGroup, Page, Section, ComponentItem, WebElement } from '../../../../../state-management/model/interfaces/state.model';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

import { DeleteNavbarGroupWarningDialogComponent } from './delete-navbar-group-warning-dialog/delete-navbar-group-warning-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { GenericDialog, DialogData } from './generic-dialog/generic-dialog.component';
import { MatDividerModule } from '@angular/material/divider';
import { v4 as uuidv4 } from 'uuid';
import { PageTemplateService } from '../../../../../admin/services/page-template.service';
import { LoggerService } from '../../../../../services/logger.service';


@Component({
  selector: 'app-website-config-form-page-administration',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    FormsModule,
    MatInputModule,
    MatCheckbox,
    MatSelect,
    MatOption,
    MatIcon,
    MatButtonModule,
    MatDividerModule,
    WebsiteConfigFormExpansionPanelComponent
  ],
  templateUrl: './website-config-form-page-administration.component.html',
  styleUrl: './website-config-form-page-administration.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WebsiteConfigFormPageAdministrationComponent implements OnInit {
  private formSubscription: Subscription = new Subscription();
  private debounceTimer: any;

  readonly dialog = inject(MatDialog);
  public metaData: Signal<BusinessConfiguration> = this.websiteState.configuration;
  public form!: FormGroup<{ pages: FormArray<FormGroup>; navbarGroups: FormArray<FormGroup>; }>;

  constructor(
    private websiteState: WebsiteStateService,
    private logger: LoggerService,
    private pageTemplateService: PageTemplateService,
  ) { }

  ngOnInit(): void {
    const initialPages: Record<string, Page> = this.websiteState.pages();
    const initialNavbarGroups = this.metaData().navbarGroups;

    this.form = new FormGroup({
      pages: this.createPageGroupsForm(initialPages),
      navbarGroups: this.createNavbarGroupsForm(initialNavbarGroups),
    });

    this.formSubscription = this.form.valueChanges.subscribe(() => {
      if (this.form.valid) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(async () => { this.#saveChanges(); }, 300);
      } else {
        this.logger.log('PAGES FORM CHANGES: Formular ungültig, keine Daten gespeichert.', this.form);
        if (this.form.errors) {
          this.logger.log('PAGES FORM Errors:', this.form.errors);
        }
      }
    });
  }

  ngOnDestroy() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  private createNavbarGroupsForm(groups: any): FormArray {
    return new FormArray(Object.values(groups).map(groups => this.createNavbarGroupsGroup(groups)))
  }

  private createNavbarGroupsGroup(navbarGroup: any): FormGroup {
    return new FormGroup({
      id: new FormControl(navbarGroup.id),
      displayName: new FormControl(navbarGroup.displayName, Validators.required),
    });
  }

  private createPageGroupsForm(pages: any): FormArray {
    return new FormArray(Object.values(pages).map(page => this.createPageGroup(page)), this.validateDuplicateUrls());
  };


  // TODO: Validator should be extended to check for duplicate Navbar Group Names
  private validateDuplicateUrls(): ValidatorFn {
    return (formArray: AbstractControl) => {
      if (!formArray || !(formArray instanceof FormArray)) return null;

      const controls = formArray.controls;
      const urlMap: Record<string, number[]> = {}; // Zum Nachverfolgen der Positionen jeder URL
      let hasErrors = false;

      // Schritt 1: URLs sammeln und Positionen speichern
      controls.forEach((control, index) => {
        const urlControl = control.get('url') as FormControl;
        const currentUrl = urlControl?.value;

        if (!currentUrl) return; // Überspringen, wenn keine URL vorhanden

        if (!urlMap[currentUrl]) {
          urlMap[currentUrl] = [];
        }
        urlMap[currentUrl].push(index); // Index speichern
      });

      // Schritt 2: Fehler zuweisen
      Object.values(urlMap).forEach((indices) => {
        if (indices.length > 1) {
          // Mehr als ein Vorkommen -> Duplikate markieren
          hasErrors = true;
          indices.forEach((index) => {
            const urlControl = controls[index].get('url') as FormControl;
            urlControl.setErrors({ duplicateUrl: 'Die URL muss eindeutig sein' });
          });

        } else {
          // Kein Duplikat -> Fehler bereinigen
          const urlControl = controls[indices[0]].get('url') as FormControl;
          if (urlControl?.hasError('duplicateUrl')) {
            urlControl.setErrors(null);
          }
        }

      });

      return hasErrors ? { duplicateUrls: 'Die URL muss eindeutig sein' } : null;
    };
  }

  private validPathValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;

      if (!value) {
        return { required: true }; // URL ist erforderlich
      }

      // Prüfe, ob die URL ungültige Zeichen enthält
      // const isValid = /^[a-zA-Z0-9_.-]+$/.test(value);
      const isValid = /^(?!\/)[a-zA-Z0-9_.\-\/]+$/.test(value);

      if (!isValid) {
        return { invalidPath: 'Keine Sonderzeichen' };
      }

      return null; // Kein Fehler
    };
  }

  private createPageGroup(page: any): FormGroup {
    return new FormGroup({
      id: new FormControl(page.id),
      name: new FormControl({ value: page.name, disabled: !page.isEditable }, [Validators.required]),
      url: new FormControl({ value: page.url, disabled: !page.isEditable }, Validators.compose([Validators.required, this.validPathValidator()])),
      isEditable: new FormControl(page.isEditable),
      isInNavbar: new FormControl({ value: page.isInNavbar || false, disabled: !page.isEditable }),
      navbarGroup: new FormControl({ value: page.navbarGroup || null, disabled: !page.isEditable }),
    });
  }

  get pagesArray(): FormArray<FormGroup> {
    return this.form.get('pages') as FormArray<FormGroup>;
  }

  get navbarGroupArray(): FormArray<FormGroup> {
    return this.form.get('navbarGroups') as FormArray<FormGroup>;
  }

  public addPage() {
    this.pagesArray.push(this.createPageGroup({
      id: uuidv4(),
      name: '',
      url: '',
      isEditable: true,
      isInNavbar: false,
      navbarGroup: null,
    }));
  }

  public removePageAtIndex(index: number): void {
    const page = this.pagesArray.at(index).value;
    if (page.isEditable) {
      this.pagesArray.removeAt(index);
    }
  }

  public addNavbarGroup(): void {
    this.navbarGroupArray.push(this.createNavbarGroupsGroup({
      id: uuidv4(),
      displayName: '',
    }));
  }

  public removeNavbarGroupAtIndex(index: number): void {
    const formArray = this.navbarGroupArray as FormArray; // Typkonvertierung
    if (index >= 0 && index < formArray.length) {
      formArray.removeAt(index);
    }
  }

  async onDeletePage(index: number): Promise<void> {
    const page: Page = this.pagesArray.value[index];
    try {
      const confirmDelete = await this.openDeletePageDialog(page.name);
      if (confirmDelete) {
        this.removePageAtIndex(index);
      } else {
        this.logger.log(`Löschen der Seite ${page.name} abgebrochen`);
      }
    } catch (error) {
      this.logger.error('Fehler beim Öffnen des Page-Delete-Dialogs:', error);
    }
  }

  public onInputChange(index: number, event: Event): void {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.updateNavbarGroup(index, event);
    }, 300);
  }

  async onDeleteNavbarGroup(index: number): Promise<void> {
    const navbarGroup: NavbarGroup = this.navbarGroupArray.value[index];
    const filteredPages: Page[] = this.pagesArray.value.filter(obj => obj.navbarGroup === navbarGroup.id)
    try {
      const confirmDelete = await this.openDeleteNavbarGroupDialog(navbarGroup, filteredPages);
      if (confirmDelete) {
        this.removeNavbarGroupAtIndex(index);
        this.deleteNavbarGroup(navbarGroup);
      } else {
        this.logger.log('Löschen abgebrochen');
      }
    } catch (error) {
      this.logger.error('Fehler beim Öffnen des Dialogs:', error);
    }
  }

  private openDeletePageDialog(pageDisplayName: string): Promise<boolean> {
    const data: DialogData = {
      title: 'Warnung',
      textChunks: [
        { text: 'Soll die Seite ' },
        { text: ` ${pageDisplayName} `, isStrong: true },
        { text: ' wirklich gelöscht werden?' }
      ],
      cancelButtonText: 'Abbrechen',
      confirmButtonText: 'Ja, löschen!',
    };

    const dialogRef = this.dialog.open(GenericDialog, {
      data: data,
      panelClass: "admin-theme"
    });
    return firstValueFrom(dialogRef.afterClosed());
  }

  private openDeleteNavbarGroupDialog(navbarGroup: NavbarGroup, filteredPages: Page[]): Promise<boolean> {
    const filteredPagesNames = filteredPages.map(obj => obj.name);
    const dialogRef = this.dialog.open(DeleteNavbarGroupWarningDialogComponent, {
      data: { displayName: navbarGroup.displayName, filteredPagesNames: filteredPagesNames }, panelClass: "admin-theme"
    });
    return firstValueFrom(dialogRef.afterClosed());
  }

  private deleteNavbarGroup(navbarGroup: NavbarGroup): void {
    for (const page of this.pagesArray.value) {
      if (page.navbarGroup === navbarGroup.id) {
        page.isInNavbar = false;
        page.navbarGroup = null;
        this.websiteState.updatePage(page.id, page);
      }
    }
    const formArray = this.pagesArray as FormArray<FormGroup>;
    formArray.controls.forEach((formGroup: FormGroup) => {
      if (formGroup.get('navbarGroup')?.value === navbarGroup.id) {
        formGroup.patchValue({
          isInNavbar: false,
          navbarGroup: null,
        });
      }
    });
    const updatedNavbarGroup = this.navbarGroupArray.value.filter(obj => obj.id !== navbarGroup.id);
    this.websiteState.updateConfiguration({ navbarGroups: updatedNavbarGroup });
  }

  private updateNavbarGroup(index: number, event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    const currentNavbarGroups: NavbarGroup[] = this.navbarGroupArray.value; // Aktuelle navbarGroups kopieren
    if (index < 0 || index >= currentNavbarGroups.length) {
      this.logger.error("Index außerhalb des gültigen Bereichs:", index);
      return;
    }
    // Aktuelles Element aktualisieren
    const updatedNavbarGroup = {
      ...currentNavbarGroups[index],
      displayName: inputValue,
    };
    currentNavbarGroups[index] = updatedNavbarGroup;
    this.websiteState.updateConfiguration({ navbarGroups: currentNavbarGroups });
  }

  #saveChanges() {
    this.websiteState.updateConfiguration(this.metaData());
    const currentPages = this.websiteState.pages();
    const formPages = this.pagesArray.value;
    this.logger.log('FORM PAGES:', formPages);
    const processedPageIds = new Set<string>();
    // 1. Bestehende oder geänderte Seiten speichern
    formPages.forEach((page: any) => {
      if (!page.isEditable) {
        processedPageIds.add(page.id); // ID zur Nachverfolgung hinzufügen
        return
      }

      if (page.id) {
        // Falls Seite existiert, update oder füge sie hinzu
        if (currentPages[page.id]) {
          // Seite existiert bereits, also nur aktualisieren
          this.websiteState.updatePage(page.id, {
            name: page.name,
            url: page.url,
            isInNavbar: page.isInNavbar,
            navbarGroup: page.navbarGroup,
          });
        } else {
          // Neue Seite hinzufügen
          this.pageTemplateService.createTemplate(page);
        }
        processedPageIds.add(page.id); // ID zur Nachverfolgung hinzufügen
      }
    });
    // 2. Entfernte Seiten löschen
    Object.keys(currentPages).forEach((pageId) => {
      if (!processedPageIds.has(pageId) && currentPages[pageId].isEditable) {
        // Entferne die Seite nur, wenn sie löschbar ist
        this.websiteState.removePage(pageId);
      }
    });
  }
}